// Zweigschrift (T38, Grafik-Lead, Runde 8; public/art/schrift/hinweise.json): Buchstaben aus Zweigen als Bildatlas.
// Größen gross (Titel), mittel (Überschriften), klein (Knöpfe); Hölzer eiche (auf hellem Grund), birke (auf dunklem),
// nuss (auf hellem Holz, Knöpfe). Ein Schriftpixel ist ein Bildschirmpixel bei 1280×720; sonst wird glatt skaliert.
// Elemente mit data-twig="<groesse>-<holz>" bekommen ihren Text als Zweigschrift; der Text bleibt für Vorleser erhalten.

type Glyph = { x: number; y: number; w: number; h: number; versatzX: number; oberkante: number; vorschub: number };
type Font = { datei: string; versalhoehe: number; mittellaenge: number; zeilenabstand: number; zeichen: Record<string, Glyph>; unterschneidung: Record<string, number> };
type Loaded = { font: Font; img: HTMLImageElement };

const BASE = './art/schrift/';
const cache = new Map<string, Promise<Loaded | null>>();

function load(name: string): Promise<Loaded | null> {
  let p = cache.get(name);
  if (!p) {
    p = fetch(`${BASE}zweig-${name}.json`)
      .then((r) => (r.ok ? (r.json() as Promise<Font>) : null))
      .then(
        (font) =>
          new Promise<Loaded | null>((resolve) => {
            if (!font) return resolve(null);
            const img = new Image();
            img.onload = () => resolve({ font, img });
            img.onerror = () => resolve(null);
            img.src = BASE + font.datei;
          }),
      )
      .catch(() => null);
    cache.set(name, p);
  }
  return p;
}

/** Einen Text als Zweigschrift auf eine Leinwand setzen (Feder auf der Grundlinie, Unterschneidung je Paar) */
function render({ font, img }: Loaded, text: string): HTMLCanvasElement {
  const glyphs: { g: Glyph; x: number }[] = [];
  let pen = 0;
  let prev = '';
  let top = 0;
  let bottom = 0;
  let left = 0;
  let right = 0;
  for (const ch of text) {
    if (prev) pen += font.unterschneidung[prev + ch] ?? 0;
    const g = font.zeichen[ch] ?? font.zeichen[ch.toUpperCase()];
    if (!g) {
      pen += font.mittellaenge * 0.6;
      prev = '';
      continue;
    }
    if (g.w > 0) {
      glyphs.push({ g, x: pen + g.versatzX });
      top = Math.max(top, g.oberkante);
      bottom = Math.max(bottom, g.h - g.oberkante);
      left = Math.min(left, pen + g.versatzX);
      right = Math.max(right, pen + g.versatzX + g.w);
    }
    pen += g.vorschub;
    prev = ch;
  }
  const c = document.createElement('canvas');
  // Platz für den weichen Schatten (1 px rechts, 2 px unten)
  c.width = Math.max(1, Math.ceil(right - left) + 2);
  c.height = Math.max(1, Math.ceil(top + bottom) + 3);
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;
  for (const { g, x } of glyphs) ctx.drawImage(img, g.x, g.y, g.w, g.h, Math.round(x - left), Math.round(top - g.oberkante), g.w, g.h);
  return c;
}

/** Wie groß ein Schriftpixel gerade ist: 1 bei 1280×720, glatt skaliert, in halben Schritten */
const pixel = () => Math.max(1, Math.min(3, Math.round((Math.min(window.innerWidth / 1280, window.innerHeight / 720) * 2)) / 2));

/** Alle Elemente mit data-twig unter root bekommen ihren Text in Zweigschrift; ohne Bilder bleibt die normale Schrift */
export function applyTwig(root: ParentNode): void {
  for (const el of root.querySelectorAll<HTMLElement>('[data-twig]')) {
    const text = (el.dataset.twigText ?? el.textContent ?? '').trim();
    if (!text || el.dataset.twigDone === text) continue;
    el.dataset.twigText = text;
    void load(el.dataset.twig!).then((f) => {
      if (!f || el.dataset.twigText !== text) return;
      const c = render(f, text);
      // Runde 7 (Rindenkarte): ein Feld mit fester Breite behält seine Buchschrift, wenn der Name nicht hineinpasst
      const max = Number(el.dataset.twigMax ?? 0);
      if (max && c.width - 2 > max) {
        el.classList.add('twig-long');
        el.dataset.twigDone = text;
        return;
      }
      c.className = 'twig-text';
      c.setAttribute('aria-hidden', 'true');
      // data-twig-px: ein Schriftpixel ist so viele CSS-Pixel groß (auf der Karte 1, die Karte skaliert als Ganzes)
      const k = el.dataset.twigPx ? Number(el.dataset.twigPx) : pixel();
      c.style.width = `${c.width * k}px`;
      c.style.height = `${c.height * k}px`;
      el.setAttribute('aria-label', text);
      el.replaceChildren(c);
      el.dataset.twigDone = text;
    });
  }
}

/** Den Text eines Elements mit Zweigschrift ändern */
export function setTwigText(el: HTMLElement, text: string): void {
  el.dataset.twigText = text;
  el.textContent = text;
  delete el.dataset.twigDone;
  applyTwig(el.parentElement ?? el);
}
